import type React from 'react';
import { useEffect, useState } from 'react';
import {
  listSnapshots,
  createSnapshot,
  activateSnapshot,
  type AdminSnapshot,
} from '../../lib/adminApi';
import { AdminPageShell, cls } from './shared';

export function SnapshotsPage() {
  const [snapshots, setSnapshots] = useState<AdminSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newSource, setNewSource] = useState('manual');
  const [newNotes, setNewNotes] = useState('');
  const [saving, setSaving] = useState(false);

  function load() {
    listSnapshots()
      .then((r) => {
        setSnapshots(r.snapshots);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to load');
        setLoading(false);
      });
  }

  useEffect(load, []);

  async function handleCreate() {
    if (!newDate) return;
    setSaving(true);
    try {
      await createSnapshot({ effective_date: newDate, source: newSource, notes: newNotes || undefined });
      setShowCreate(false);
      setNewDate('');
      setNewNotes('');
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error creating snapshot');
    } finally {
      setSaving(false);
    }
  }

  async function handleActivate(s: AdminSnapshot) {
    if (!confirm(`Set all layouts and products to price snapshot "${s.effectiveDate}" (${s.source})? This updates ${snapshots.length > 0 ? 'all' : '0'} records.`)) return;
    try {
      const result = await activateSnapshot(s.id);
      alert(`Updated ${result.layoutsUpdated} layouts, ${result.productsUpdated} products.`);
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Activation failed');
    }
  }

  if (loading) return <AdminPageShell title="Price Snapshots"><p className="text-vari-gray text-sm">Loading…</p></AdminPageShell>;
  if (error) return <AdminPageShell title="Price Snapshots"><p className="text-red-600 text-sm">{error}</p></AdminPageShell>;

  return (
    <AdminPageShell
      title="Price Snapshots"
      actions={
        <button
          onClick={() => setShowCreate(true)}
          className={cls.btnPrimary}
        >
          + New Snapshot
        </button>
      }
    >
      {showCreate && (
        <div className={cls.formPanel}>
          <p className="text-sm font-medium text-vari-navy mb-3">New Snapshot</p>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <label className="text-xs text-vari-gray block mb-1">Effective Date</label>
              <input
                type="date"
                className={cls.input}
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-vari-gray block mb-1">Source</label>
              <select
                className={cls.select + ' w-full'}
                value={newSource}
                onChange={(e) => setNewSource(e.target.value)}
              >
                <option value="manual">manual</option>
                <option value="salesforce">salesforce</option>
                <option value="sap">sap</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-vari-gray block mb-1">Notes</label>
              <input
                className={cls.input}
                value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => void handleCreate()} disabled={saving} className={cls.btnSmPrimary}>
              {saving ? 'Saving…' : 'Create'}
            </button>
            <button onClick={() => setShowCreate(false)} className={cls.btnGhost}>Cancel</button>
          </div>
        </div>
      )}

      <div className={cls.tableWrap}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-vari-soft-gray">
            <tr>
              <th className={cls.th}>Effective Date</th>
              <th className={cls.th}>Source</th>
              <th className={cls.th}>Notes</th>
              <th className={cls.th}>Created</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {snapshots.map((s, i) => (
              <tr key={s.id} className={cls.tr(i % 2 === 1)}>
                <td className={`${cls.td} font-medium`}>{s.effectiveDate}</td>
                <td className={`${cls.td} text-vari-gray`}>{s.source}</td>
                <td className={`${cls.td} text-vari-gray`}>{s.notes ?? '—'}</td>
                <td className={`${cls.td} text-vari-gray`}>{new Date(s.createdAt).toLocaleDateString()}</td>
                <td className={cls.tdRight}>
                  <button
                    onClick={() => void handleActivate(s)}
                    className={cls.btnOutline}
                  >
                    Set active
                  </button>
                </td>
              </tr>
            ))}
            {snapshots.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-vari-gray text-sm">No snapshots.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminPageShell>
  );
}
